"use client";

import { Button, Flex, Typography } from "antd/lib";
import { App, theme } from "antd";
import { QRCodeCanvas } from "qrcode.react";
import React, { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { analytics } from "@/constants/common";
import { openInNewTab } from "@/utils/helpers";

const { Title, Text } = Typography;

const OPENED_QR_BACKGROUND = "#FFFFFF";
const EXPORT_QR_SIZE = 500;

const escapeHtml = (input: string) =>
  input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const escapeForScript = (input: string) => JSON.stringify(input).replace(/</g, "\\u003c");

const formatSessionCode = (input: string) => (input.match(/.{1,4}/g) ?? [input]).join("-");

const sanitizeFileName = (input: string) =>
  input
    .replace(/[\\/:*?"<>|\x00-\x1f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

interface EventQrCodeProps {
  value: string;
  eventId: string;
  eventTitle: string;
  size?: number;
}

const EventQrCode: React.FC<EventQrCodeProps> = ({ value, eventId, eventTitle, size = 160 }) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const titleText = `${eventTitle} — QR`;
  const boldComponents = { b: <Text strong /> };

  const copySessionCode = async () => {
    try {
      await navigator.clipboard.writeText(value);
      analytics.track("Group code copied", { value: JSON.stringify({ eventId, eventTitle }) });
      message.success(t("eventDetailsPage.messages.sessionCodeCopied"));
    } catch {
      message.error(t("common.messages.linkCopyFail"));
    }
  };

  const openQrInNewTab = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataUrl = canvas.toDataURL("image/png");
    const sessionCodeLabel = t("eventDetailsPage.qrSessionCodeLabel");
    const sessionCodeHint = t("eventDetailsPage.qrSessionCodeHintDirect");
    const copyLabel = t("eventDetailsPage.buttons.copySessionCode");
    const copiedLabel = t("eventDetailsPage.messages.sessionCodeCopied");
    const copyFailLabel = t("common.messages.linkCopyFail");
    const html = `<!doctype html><meta charset="utf-8"><title>${escapeHtml(titleText)}</title><body style="margin:0;min-height:100vh;display:flex;flex-direction:column;align-items:center;justify-content:center;background:${OPENED_QR_BACKGROUND};font-family:system-ui,-apple-system,sans-serif;gap:24px;padding:24px;box-sizing:border-box"><img src="${dataUrl}" alt="QR" width="${EXPORT_QR_SIZE}" height="${EXPORT_QR_SIZE}"><div style="display:flex;flex-direction:column;align-items:center;gap:8px;max-width:90vw"><div style="font-size:20px;color:#666;font-weight:500">${escapeHtml(sessionCodeLabel)}</div><div style="display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:center"><div style="font-size:32px;font-weight:600;text-align:center;word-break:break-all;color:#000">${escapeHtml(formatSessionCode(value))}</div><button id="copy-btn" type="button" aria-label="${escapeHtml(copyLabel)}" style="display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;padding:0;background:transparent;border:none;cursor:pointer;color:#006D67;outline:none"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg></button></div><div style="font-size:14px;color:#888;text-align:center;max-width:480px;line-height:1.4">(${escapeHtml(sessionCodeHint)})</div></div><div id="toast" role="status" aria-live="polite" style="position:fixed;top:24px;left:50%;transform:translateX(-50%);padding:10px 16px;background:#FFFFFF;border-radius:8px;box-shadow:0 6px 16px 0 rgba(0,0,0,0.08),0 3px 6px -4px rgba(0,0,0,0.12),0 9px 28px 8px rgba(0,0,0,0.05);font-size:14px;color:rgba(0,0,0,0.88);display:none;align-items:center;gap:8px;pointer-events:none;z-index:1000"><span id="toast-icon" style="font-weight:bold;font-size:16px"></span><span id="toast-text"></span></div><script>(function(){const v=${escapeForScript(value)};const copied=${escapeForScript(copiedLabel)};const failed=${escapeForScript(copyFailLabel)};const btn=document.getElementById("copy-btn");const toast=document.getElementById("toast");const toastIcon=document.getElementById("toast-icon");const toastText=document.getElementById("toast-text");if(!btn||!toast||!toastIcon||!toastText)return;let timer=null;function showToast(text,success){toastIcon.textContent=success?"✓":"⚠";toastIcon.style.color=success?"#52C41A":"#FF4D4F";toastText.textContent=text;toast.style.display="flex";if(timer)clearTimeout(timer);timer=setTimeout(function(){toast.style.display="none";},3000);}btn.addEventListener("click",function(){navigator.clipboard.writeText(v).then(function(){showToast(copied,true);}).catch(function(){showToast(failed,false);});});})();</script></body>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    openInNewTab(url);
    analytics.track("Event QR opened", { value: JSON.stringify({ eventId, eventTitle }) });
  };

  const download = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = `${sanitizeFileName(titleText) || "event-qr-code"}.png`;
    link.click();
    analytics.track("Event QR downloaded", { value: JSON.stringify({ eventId, eventTitle }) });
  };

  return (
    <Flex vertical gap={16}>
      <Title level={4} style={{ marginBottom: 0 }}>
        {t("eventDetailsPage.qrCode")}
      </Title>
      <Flex vertical gap={4}>
        <Text>
          <Trans i18nKey="eventDetailsPage.qrInstructions" components={boldComponents} />
        </Text>
        <ol style={{ margin: 0, paddingInlineStart: 20 }}>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep1" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep2" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep3" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep4" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep5" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>{t("eventDetailsPage.qrStep6")}</Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep7" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep8" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrStep9" components={boldComponents} />
            </Text>
          </li>
        </ol>
        <Text>{t("eventDetailsPage.qrAfterSteps")}</Text>
        <Text strong style={{ marginTop: 8 }}>
          {t("eventDetailsPage.qrFallbackHeading")}
        </Text>
        <ol style={{ margin: 0, paddingInlineStart: 20 }}>
          <li>
            <Text>
              <Trans i18nKey="eventDetailsPage.qrFallbackStep1" components={boldComponents} />
            </Text>
          </li>
          <li>
            <Text>{t("eventDetailsPage.qrFallbackStep2")}</Text>
          </li>
        </ol>
      </Flex>
      <QRCodeCanvas
        ref={canvasRef}
        value={value}
        size={EXPORT_QR_SIZE}
        level="H"
        style={{ width: size, height: size }}
      />
      <Flex align="center" gap={8} wrap>
        <Text style={{ wordBreak: "break-all" }}>
          {t("eventDetailsPage.qrSessionCodeLabel")} {formatSessionCode(value)}
        </Text>
        <CopySessionCodeIcon onCopy={copySessionCode} ariaLabel={t("eventDetailsPage.messages.sessionCodeCopied")} />
      </Flex>
      <Flex gap={16}>
        <Button onClick={openQrInNewTab}>{t("eventDetailsPage.buttons.openQrInNewTab")}</Button>
        <Button onClick={download}>{t("eventDetailsPage.buttons.downloadQr")}</Button>
      </Flex>
    </Flex>
  );
};

const CopySessionCodeIcon: React.FC<{ onCopy: () => Promise<void>; ariaLabel: string }> = ({ onCopy, ariaLabel }) => {
  const [isHover, setIsHover] = useState(false);
  const { token } = theme.useToken();

  const handleClick = () => {
    void onCopy();
  };

  return (
    <i
      className="icon-link_stroke"
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      aria-label={ariaLabel}
      style={{
        cursor: "pointer",
        padding: 0,
        outline: "none",
        color: isHover ? token.colorPrimary : undefined,
      }}
    />
  );
};

export default EventQrCode;
